import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { PageHero } from '../components/layout/PageHero';
import { configurationLayers, configurationStatus, howItWorksFunctions, publicModules } from '../data/howItWorks';
import { fadeUp } from '../utils/motion';
export function HowItWorksPage() {
  return <>
      <PageHero eyebrow="How It Works" title="Connect knowledge. Coordinate work. Develop capability." lead="Noetivra brings the tools your organization needs into dedicated workspaces. Choose a solution, configure the relevant modules and connect the knowledge and services that support your work." data-id="element-2874" data-mp="2838khl2ms7s2">
        <a href="/solutions" className="mt-8 inline-flex items-center justify-center gap-2 bg-navy hover:bg-navy-light text-white font-semibold px-6 py-3.5 rounded-md transition-colors duration-150 whitespace-nowrap" data-id="element-2875" data-mp="1pc8t6d7nylvm">
          Find your solution <ArrowRight size={18} aria-hidden="true" data-id="element-2876" data-mp="30t8jx4ed4snj" />
        </a>
      </PageHero>

      <section aria-label="Three connected functions" className="bg-white border-b border-line" data-id="element-2877" data-mp="1svmovxpr4333">
        <ol className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2878" data-mp="1xto6sfbfxwcz">
          {howItWorksFunctions.map((item, index) => <motion.li key={item.id} id={item.id} {...fadeUp} className="scroll-mt-24 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-16 border-t border-line first:border-t-0 py-14 lg:py-16" data-id="element-2879" data-mp="22s7tihxuzc6e" data-mpi={index}>
              <div className="lg:col-span-5" data-id="element-2880" data-mp="1f5syvc2ifcrs">
                <p className="font-heading font-bold text-sm text-teal mb-3" aria-hidden="true" data-id="element-2881" data-mp="1f2dj0710hzpb">
                  {index + 1}
                </p>
                <h2 className="font-heading font-bold text-3xl lg:text-4xl text-ink-dark tracking-tight leading-tight" data-id="element-2882" data-mp="1ny3bt14shwcq">{item.title}</h2>
              </div>
              <div className="lg:col-span-7 lg:pt-8" data-id="element-2883" data-mp="rdcfmy71deqw">
                <p className="text-xl text-ink-dark leading-relaxed" data-id="element-2884" data-mp="vffvdz0qn5hz">{item.body}</p>
                <p className="mt-5 text-ink-muted leading-relaxed" data-id="element-2885" data-mp="1yqwhyw1p4xis">
                  <span className="font-semibold text-ink-dark" data-id="element-2886" data-mp="1tsl84lbein0k">For example: </span>
                  {item.example}
                </p>
                <a href={item.link.href} className="mt-6 inline-flex items-center gap-2 font-semibold text-navy hover:text-navy-light transition-colors duration-150 whitespace-nowrap" data-id="element-2887" data-mp="28gfznydoalc2">
                  {item.link.label} <ArrowRight size={16} aria-hidden="true" data-id="element-2888" data-mp="1atpcr6nt42l5" />
                </a>
              </div>
            </motion.li>)}
        </ol>
      </section>

      <section id="modules" aria-labelledby="configured-title" className="scroll-mt-20 bg-surface-bg border-b border-line py-20 lg:py-24" data-id="element-2889" data-mp="1ov1r5dnbjw4x">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2890" data-mp="308ndys5phlna">
          <motion.div {...fadeUp} className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-16 mb-12" data-id="element-2891" data-mp="306u83f2x35gc">
            <h2 id="configured-title" className="lg:col-span-5 font-heading font-bold text-3xl lg:text-4xl text-ink-dark tracking-tight leading-tight" data-id="element-2892" data-mp="2w6sc9mjv568z">
              Configured around your needs
            </h2>
            <p className="lg:col-span-7 lg:pt-2 text-lg text-ink-dark leading-relaxed" data-id="element-2893" data-mp="38nbb2vfrdglv">
              Every solution is built from the same four layers, so a research team and a public agency share one
              foundation while working in very different ways.
            </p>
          </motion.div>

          <motion.dl {...fadeUp} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-line border border-line rounded-lg overflow-hidden" data-id="element-2894" data-mp="3mjf3cj72nevp">
            {configurationLayers.map(layer => <div key={layer.name} className="bg-white p-6" data-id="element-2895" data-mp="3hqcfmroz6znf">
                <dt className="font-heading font-bold text-lg text-ink-dark" data-id="element-2896" data-mp="3horj4wj3p6mx">
                  {layer.name === 'Core' || layer.name === 'Workspace' ? `Noetivra ${layer.name}` : layer.name}
                </dt>
                <dd className="mt-2 text-ink-muted leading-relaxed" data-id="element-2897" data-mp="e3c5141un2k1">{layer.body}</dd>
              </div>)}
          </motion.dl>

          <motion.div {...fadeUp} className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16" data-id="element-2898" data-mp="2hipiajft716v">
            <h3 className="lg:col-span-5 font-heading font-bold text-xl text-ink-dark" data-id="element-2899" data-mp="2cjmcgws08qxu">Shared modules</h3>
            <div className="lg:col-span-7" data-id="element-2900" data-mp="27lulc4z6p8up">
              <table className="w-full text-left" data-id="element-2901" data-mp="27mvziibhybgn">
                <caption className="sr-only" data-id="element-2902" data-mp="35930li8ldlo9">Public module examples and their purpose</caption>
                <thead data-id="element-2903" data-mp="3096y5nfjm2at">
                  <tr className="border-b border-ink-dark/70" data-id="element-2904" data-mp="30b1iazizpqdu">
                    <th scope="col" className="w-1/3 py-3 pr-4 text-sm font-semibold text-ink-dark" data-id="element-2905" data-mp="2viqf9nsydoh2">Module</th>
                    <th scope="col" className="py-3 text-sm font-semibold text-ink-dark" data-id="element-2906" data-mp="2qkp03dyne9av">Purpose</th>
                  </tr>
                </thead>
                <tbody data-id="element-2907" data-mp="3o0m37nypexpr">
                  {publicModules.map(module => <tr key={module.name} className="border-b border-line align-top" data-id="element-2908" data-mp="3j7szn3ez1vf9">
                      <th scope="row" className="py-4 pr-4 font-heading font-semibold text-ink-dark" data-id="element-2909" data-mp="3ria222i5eu7s">{module.name}</th>
                      <td className="py-4 text-ink-muted leading-relaxed" data-id="element-2910" data-mp="3vjc3mpzjficx">{module.purpose}</td>
                    </tr>)}
                </tbody>
              </table>
              <p className="mt-6 border-l-2 border-teal pl-4 text-sm text-ink-muted leading-relaxed" data-id="element-2911" data-mp="9cnemzymdrst">{configurationStatus}</p>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="bg-white py-20 lg:py-24" data-id="element-2912" data-mp="ib8qkc9l68km">
        <motion.div {...fadeUp} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-16" data-id="element-2913" data-mp="i9xnyfrvn6v3">
          <h2 className="lg:col-span-5 font-heading font-bold text-3xl text-ink-dark tracking-tight leading-tight" data-id="element-2914" data-mp="s2d3z7jm5ldc">Go deeper</h2>
          <ul className="lg:col-span-7 border-t border-line" data-id="element-2915" data-mp="x2rdyv4rz5e0">
            {[{
            label: 'Semantic Knowledge Platform',
            href: '/semantic-knowledge'
          }, {
            label: 'Noetivra',
            href: '/noetivra'
          }, {
            label: 'Human Capability Framework, CJI and NCJI',
            href: '/human-capability'
          }, {
            label: 'LLM vs. SKP: what’s the difference?',
            href: '/llm-vs-skp'
          }, {
            label: 'Compare all demonstrations',
            href: '/noetivra/demo-center'
          }].map(link => <li key={link.href} className="border-b border-line" data-id="element-2916" data-mp="h95j59k3j6jt">
                <a href={link.href} className="group flex items-center justify-between gap-4 py-4 font-heading font-semibold text-ink-dark hover:text-navy transition-colors duration-150" data-id="element-2917" data-mp="v1bzavn91dj9">
                  {link.label}
                  <ArrowRight size={16} aria-hidden="true" className="shrink-0 text-ink-muted group-hover:text-navy transition-colors duration-150" data-id="element-2918" data-mp="1016rr3moab6f" />
                </a>
              </li>)}
          </ul>
        </motion.div>
      </section>
    </>;
}